import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

export function PageShell({ children }) {
  return (
    <div className="bg-white dark:bg-gray-900 text-slate-900 dark:text-white overflow-x-hidden">
      {children}
    </div>
  );
}

export function SectionLabel({ children }) {
  return (
    <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase rounded-full hero-glass-tag text-[#2563EB]">
      {children}
    </span>
  );
}

export function GlowBackground({ variant = 'default' }) {
  const accents = {
    default: ['bg-[#2563EB]/15', 'bg-cyan-400/10'],
    security: ['bg-red-500/10', 'bg-[#2563EB]/12'],
    education: ['bg-violet-500/10', 'bg-cyan-400/10'],
    network: ['bg-emerald-500/10', 'bg-[#2563EB]/12'],
  };
  const [a, b] = accents[variant] || accents.default;

  return (
    <>
      <div className={`absolute top-1/4 -left-32 w-96 h-96 ${a} rounded-full blur-3xl animate-pulse`} />
      <div className={`absolute bottom-0 right-0 w-[500px] h-[500px] ${b} rounded-full blur-3xl`} />
    </>
  );
}

export function PremiumHero({
  badge,
  title,
  highlight,
  subtitle,
  stats = [],
  actions = [],
  variant = 'default',
}) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section
      ref={ref}
      className="relative pt-28 pb-16 md:pt-32 md:pb-24 px-6 bg-gradient-to-b from-white via-slate-50/90 to-white dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 overflow-hidden"
    >
      <GlowBackground variant={variant} />
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.65 }}
        className="relative max-w-5xl mx-auto text-center"
      >
        <SectionLabel>{badge}</SectionLabel>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
          <span className="text-slate-900 dark:text-white">{title} </span>
          {highlight && (
            <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
              {highlight}
            </span>
          )}
        </h1>
        {subtitle && (
          <p className="text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            {subtitle}
          </p>
        )}

        {stats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15 + i * 0.08 }}
                className="hero-glass-card rounded-2xl px-4 py-5 text-center"
              >
                <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#2563EB] to-cyan-500 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-slate-500 dark:text-gray-400 mt-1 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        )}

        {actions.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4">
            {actions.map((action, i) => {
              const className = action.primary
                ? 'inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all'
                : 'inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-gray-200 font-medium hover:border-[#2563EB]/40 hover:text-[#2563EB] transition-all hero-glass-tag';

              if (action.onClick) {
                return (
                  <button key={i} type="button" onClick={action.onClick} className={className}>
                    {action.label}
                    {action.primary && <ArrowRightIcon className="w-5 h-5" />}
                  </button>
                );
              }
              if (action.external) {
                return (
                  <a key={i} href={action.href} className={className}>
                    {action.label}
                    {action.primary && <ArrowRightIcon className="w-5 h-5" />}
                  </a>
                );
              }
              return (
                <Link key={i} to={action.href} className={className}>
                  {action.label}
                  {action.primary && <ArrowRightIcon className="w-5 h-5" />}
                </Link>
              );
            })}
          </div>
        )}
      </motion.div>
    </section>
  );
}

export function SectionHeader({ badge, title, highlight, subtitle, className = '' }) {
  return (
    <div className={`text-center mb-14 ${className}`}>
      {badge && <SectionLabel>{badge}</SectionLabel>}
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
        {title}{' '}
        {highlight && <span className="text-[#2563EB]">{highlight}</span>}
      </h2>
      {subtitle && (
        <p className="text-slate-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

export function PremiumSection({ children, className = '', id, alt = false }) {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });

  return (
    <section
      id={id}
      ref={ref}
      className={`py-20 md:py-28 px-6 relative ${alt ? 'bg-gradient-to-b from-slate-50/90 to-white dark:from-gray-800/50 dark:to-gray-900' : ''} ${className}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto relative"
      >
        {children}
      </motion.div>
    </section>
  );
}

export function GlassCard({ children, className = '', hover = true }) {
  return (
    <div
      className={`hero-glass-card rounded-2xl ${hover ? 'hover:shadow-[0_20px_50px_-15px_rgba(37,99,235,0.15)] hover:-translate-y-1' : ''} transition-all duration-300 ${className}`}
    >
      {children}
    </div>
  );
}

export function FeatureGrid({ items, columns = 3 }) {
  const cols = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'lg:grid-cols-4 md:grid-cols-2' };

  return (
    <div className={`grid grid-cols-1 ${cols[columns] || cols[3]} gap-5 md:gap-6`}>
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            whileHover={{ y: -6 }}
            className="group relative"
          >
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#2563EB]/0 via-cyan-400/0 to-[#2563EB]/0 group-hover:from-[#2563EB]/20 group-hover:via-cyan-400/10 group-hover:to-blue-600/20 rounded-2xl transition-all duration-500 blur-sm opacity-0 group-hover:opacity-100" />
            <GlassCard className="relative p-6 md:p-7 h-full">
              {Icon && (
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB]/10 to-cyan-500/10 border border-blue-100 dark:border-blue-900/40 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-[#2563EB]" />
                </div>
              )}
              {item.badge && (
                <span className="inline-block px-2.5 py-0.5 mb-3 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#2563EB]/10 text-[#2563EB]">
                  {item.badge}
                </span>
              )}
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 group-hover:text-[#2563EB] transition-colors">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{item.description}</p>
              {item.tags && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-300 border border-slate-200/80 dark:border-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </GlassCard>
          </motion.div>
        );
      })}
    </div>
  );
}

export function PremiumCTA({ title, subtitle, primary, secondary }) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section ref={ref} className="py-20 px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#2563EB] via-blue-600 to-cyan-600" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        className="max-w-4xl mx-auto text-center relative z-10"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{title}</h2>
        <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">{subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {primary && (
            <Link
              to={primary.href}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#2563EB] font-semibold rounded-xl shadow-lg hover:bg-slate-50 transition-all"
            >
              {primary.label}
            </Link>
          )}
          {secondary && (
            <Link
              to={secondary.href}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/80 text-white font-semibold rounded-xl hover:bg-white/10 transition-all"
            >
              {secondary.label}
            </Link>
          )}
        </div>
      </motion.div>
    </section>
  );
}

export const inputClass =
  'w-full px-4 py-3 rounded-xl bg-white/80 dark:bg-gray-800/80 border border-slate-200 dark:border-gray-600 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2563EB]/50 focus:border-[#2563EB] transition-all';

export const btnPrimaryClass =
  'inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white font-semibold rounded-xl hero-glow hover:-translate-y-0.5 transition-all';

export const filterPillClass = (active) =>
  `px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
    active
      ? 'bg-[#2563EB] text-white shadow-lg shadow-blue-500/20'
      : 'hero-glass-tag text-slate-600 dark:text-gray-300 hover:text-[#2563EB]'
  }`;
